import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Select, Upload, Switch, message, Card, DatePicker, Spin, Tag, Checkbox, Input, List, Empty } from 'antd';
import { getAuthHeaders, getAuthToken, getAuthHeadersForUpload } from '../utils/authHeaders';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Dayjs } from 'dayjs';
import { PlusOutlined, UploadOutlined, DeleteOutlined, LoadingOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,CaretRightOutlined, ExclamationCircleOutlined, ClearOutlined, FileTextOutlined, SettingOutlined } from '@ant-design/icons';
import { FileClock, FileCheck, CircleX, Loader } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import SmartSearch from '../components/SmartSearch';
import '../styles/InsurancePage.css';
const BACKEND_URL = '/api/demographics';

const { Option } = Select;

// Sortable Item Component
interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onToggle: (key: string, checked: boolean) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, label, checked, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isDragging ? '#f0f0f0' : 'transparent',
        border: isDragging ? '1px solid #d9d9d9' : '1px solid transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <div 
        {...attributes}
        {...listeners}
        style={{ 
          marginRight: '8px',
          cursor: 'grab',
          color: '#8c8c8c',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e8e8e8'
        }}
      >
        ⋮⋮
      </div>
      <Checkbox
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(id, e.target.checked);
        }}
        style={{ marginRight: '8px' }}
        onClick={(e) => e.stopPropagation()}
      />
      <span 
        style={{ 
          fontSize: '13px',
          color: '#262626',
          cursor: 'pointer',
          userSelect: 'none',
          flex: 1
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(id, !checked);
        }}
      >
        {label}
      </span>
    </div>
  );
};

interface DemographicsData {
  id: string;
  doctorName: string;
  procedureName: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: string;
  surgeryDate: string;
  uploadedOn: string;
  lastModified: string;
  filename: string;
  contentType: string;
  onDemand: boolean;
  status: 'Pending' | 'In Progress' | 'Accepted' | 'Rejected';
  isDuplicate: boolean;
  rejectedBy?: string;
  rejectedReason?: string;
}

interface Note {
  _id: string;
  orderId: string;
  text: string;
  addedBy: string;
  createdAt: string;
}


interface Doctor {
  employeeId: string;
  name: string;
  specializations: string[];
}

interface FileUploadState {
  selectedFile: File | null;
  isUploading: boolean;
  uploadStatus: string;
  selectedDoctor: string;
  selectedProcedure: string;
  enableOnDemand: boolean;
  uploadError: string | null;
}

const VerificationsPage: React.FC = () => {
  const [demographicsData, setDemographicsData] = useState<DemographicsData[]>([]);
  const [filteredData, setFilteredData] = useState<DemographicsData[]>([]);
  const [loading, setLoading] = useState(false);
  const [smartSearchTerm, setSmartSearchTerm] = useState('');
  const [smartSearchField, setSmartSearchField] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [allRecordsCount, setAllRecordsCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalKey, setModalKey] = useState(0); // Add this to force Upload component reset
  const [isResubmitModalVisible, setIsResubmitModalVisible] = useState(false);
  const [resubmitRecord, setResubmitRecord] = useState<DemographicsData | null>(null);
  const [resubmitFileState, setResubmitFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    enableOnDemand: false,
    uploadError: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updatingOnDemand, setUpdatingOnDemand] = useState<string | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availableProcedures, setAvailableProcedures] = useState<string[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [statusStats, setStatusStats] = useState({
    'Pending': 0,
    'In Progress': 0,
    'Accepted': 0,
    'Rejected': 0
  });
  const [selectedStatus, setSelectedStatus] = useState<string | null>('Pending');
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DemographicsData | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [insuranceFileInfo, setInsuranceFileInfo] = useState<Record<string, any>>({});
  const [isInsuranceUploadModalVisible, setIsInsuranceUploadModalVisible] = useState(false);
  const [selectedRecordForInsurance, setSelectedRecordForInsurance] = useState<DemographicsData | null>(null);
  const [insuranceFileState, setInsuranceFileState] = useState<{
    selectedFile: File | null;
    isUploading: boolean;
    uploadError: string | null;
  }>({
    selectedFile: null,
    isUploading: false,
    uploadError: null
  });
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    id: true,
    doctorName: true,
    patientName: true,
    surgeryDate: true,
    onDemand: true,
    status: true,
    actions: true
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'id',
    'doctorName',
    'patientName',
    'surgeryDate',
    'onDemand',
    'status',
    'actions'
  ]);

  // Table ref for clearing filters and sorting
  const tableRef = useRef<any>(null);

  // Key to force table re-render when clearing filters/sorting
  const [tableKey, setTableKey] = useState(0);


  const [fileState, setFileState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
    uploadStatus: '',
    selectedDoctor: '',
    selectedProcedure: '',
    enableOnDemand: false,
    uploadError: null
  });


  // Fetch notes for a specific order
  const fetchNotes = async (orderId: string) => {
    setNotesLoading(true);
    try {
      const response = await fetch(`/api/notes/${orderId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.data || []);
      } else {
        message.error('Failed to fetch notes');
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Error fetching notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  // Add a new note
  const addNote = async (orderId: string, text: string) => {
    setAddingNote(true);
    try {
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderId, text })
      });
      if (response.ok) {
        message.success('Note added successfully');
        setNewNoteText('');
        // Refresh notes list
        await fetchNotes(orderId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      message.error('Error adding note');
    } finally {
      setAddingNote(false);
    }
  };

  // Delete a note
  const deleteNote = async (noteId: string, orderId: string) => {
    try {
      const response = await fetch(`/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        message.success('Note deleted successfully');
        // Refresh notes list
        await fetchNotes(orderId);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Error deleting note');
    }
  };

  // Fetch demographics data with server-side pagination
  const fetchDemographicsData = async (page: number = currentPage, limit: number = pageSize, search: string = '', searchField: string = 'all', status: string = '') => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy: 'uploadedOn',
        sortOrder: 'desc'
      });

      if (search) {
        params.append('search', search);
        params.append('searchField', searchField);
      }
      if (status) {
        params.append('status', status);
      }

      const response = await fetch(`${BACKEND_URL}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const demographics = data.data || [];
        const pagination = data.pagination || {};

        // Update pagination info
        setTotalCount(pagination.totalCount || 0);

        // Apply client-side sorting for onDemand priority (since server doesn't handle this complex sorting)
        const sortedDemographics = [...demographics].sort((a: DemographicsData, b: DemographicsData) => {
          // First sort by onDemand (true values first)
          if (a.onDemand !== b.onDemand) {
            return b.onDemand ? 1 : -1;
          }
          // Then sort by timestamp (newest first) - this should already be handled by server
          return new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime();
        });

        setDemographicsData(sortedDemographics);
        setFilteredData(sortedDemographics);
      } else {
        message.error('Failed to fetch demographics data');
      }
    } catch (error) {
      message.error('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Fetch status statistics
  const fetchStatusStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStatusStats(data.data || {
          'Pending': 0,
          'In Progress': 0,
          'Accepted': 0,
          'Rejected': 0
        });
      } else {
        message.error('Failed to fetch status statistics');
      }
    } catch (error) {
      message.error('Error fetching status statistics');
    }
  };

  // Fetch total count statistics
  const fetchTotalCountStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/total-count-stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllRecordsCount(data.data?.totalCount || 0);
      } else {
        message.error('Failed to fetch total count statistics');
      }
    } catch (error) {
      message.error('Error fetching total count statistics');
    }
  };

  // Handle status filter click
  const handleStatusFilterClick = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1); // Reset to first page
  };

  // Fetch doctors from database
  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.data || []);
      } else {
        message.error('Failed to fetch doctors');
      }
    } catch (error) {
      message.error('Error fetching doctors');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Fetch procedures by doctor
  const fetchProceduresByDoctor = async (doctorId: string) => {
    setLoadingProcedures(true);
    try {
      const response = await fetch(`${BACKEND_URL}/doctors/${doctorId}/procedures`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableProcedures(data.data || []);
      } else {
        message.error('Failed to fetch procedures');
        setAvailableProcedures([]);
      }
    } catch (error) {
      message.error('Error fetching procedures');
      setAvailableProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  useEffect(() => {
    fetchDoctors(); // Fetch doctors on component mount
    fetchStatusStats(); // Fetch status stats on component mount
    fetchTotalCountStats(); // Fetch total count stats on component mount
  }, []);

  // Fetch insurance info for In Progress, Accepted, and Rejected records
  useEffect(() => {
    const recordsWithInsurance = demographicsData.filter(record => 
      record.status === 'In Progress' || record.status === 'Accepted' || record.status === 'Rejected'
    );
    recordsWithInsurance.forEach(record => {
      fetchInsuranceInfo(record.id);
    });
  }, [demographicsData]);

  // Handle click outside to close columns dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the columns dropdown and button
      if (showColumnsDropdown && 
          !target.closest('[data-columns-dropdown]') && 
          !target.closest('[data-columns-button]')) {
        setShowColumnsDropdown(false);
      }
    };

    if (showColumnsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnsDropdown]);

  // Handle smart search
  const handleSmartSearch = (searchTerm: string, selectedField: string = 'all') => {
    console.log('🔄 handleSmartSearch: called with searchTerm:', searchTerm, 'selectedField:', selectedField, 'current smartSearchTerm:', smartSearchTerm);
    
    // Only reset to first page if the search term has actually changed
    const hasSearchTermChanged = searchTerm !== smartSearchTerm;
    const isNewSearch = searchTerm.trim() !== '' && hasSearchTermChanged;
    
    setSmartSearchTerm(searchTerm);
    setSmartSearchField(selectedField);
    
    if (isNewSearch) {
      console.log('🔄 handleSmartSearch: setCurrentPage(1) called because searchTerm changed to:', searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    } else {
      console.log('🔄 handleSmartSearch: NOT resetting page because searchTerm unchanged or empty');
    }
  };

  // Handle OnDemand toggle change
  const handleOnDemandChange = async (id: string, checked: boolean) => {
    setUpdatingOnDemand(id);
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/on-demand`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          onDemand: checked
        }),
      });

      if (response.ok) {
        message.success('On-demand status updated successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data with updated sorting
        fetchStatusStats(); // Refresh status statistics
        fetchTotalCountStats(); // Refresh total count stats
      } else {
        message.error('Failed to update on-demand status');
      }
    } catch (error) {
      message.error('Error updating on-demand status');
    } finally {
      setUpdatingOnDemand(null);
    }
  };

  // Update filteredData when demographicsData changes (for server-side pagination)
  useEffect(() => {
    setFilteredData(demographicsData);
  }, [demographicsData]);

  // Fetch paginated data when filters or pagination change
  useEffect(() => {
    fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
  }, [currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus]);

  // Handle PDF viewing
  const handleViewPDF = (id: string) => {
    const viewerUrl = `/view-pdf/demographics/${id}`;
    window.open(viewerUrl, '_blank');
  };



     // Handle surgery date change directly
   const handleSurgeryDateChange = async (id: string, newDate: Dayjs | null) => {
     try {
       // Find the current record to get the uploaded date
       const currentRecord = demographicsData.find(item => item.id === id);
       if (!currentRecord) {
         message.error('Record not found');
         return;
       }

       // Validation: Check surgery date against upload date
       if (newDate) {
         const uploadedDate = dayjs(currentRecord.uploadedOn);
         const surgeryDate = dayjs(newDate);

         if (surgeryDate.isBefore(uploadedDate)) {
           // Show warning but allow update to proceed
           message.warning('Surgery date is before the upload date. Please verify this information.');
         }
         // If surgery date >= upload date, no warning needed
       }

       const response = await fetch(`${BACKEND_URL}/${id}`, {
         method: 'PUT',
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify({
           surgeryDate: newDate ? newDate.toISOString() : null
         }),
       });

       if (response.ok) {
         message.success('Surgery date updated successfully');
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
       } else {
         message.error('Failed to update surgery date');
       }
     } catch (error) {
       message.error('Error updating surgery date');
     }
   };

  // Handle delete patient
  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const responseData = await response.json();
        message.success(responseData.message);
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
        fetchStatusStats(); // Refresh status statistics to update counts
        fetchTotalCountStats(); // Refresh total count stats
      } else {
        message.error('Failed to delete patient');
      }
    } catch (error) {
      message.error('Error deleting patient');
    }
  };

  // Handle delete with confirmation
  const handleDeleteWithConfirmation = (id: string) => {
    Modal.confirm({
      title: 'Delete Patient Record',
      content: 'This operation cannot be reverted. Are you sure you want to delete this patient record?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => handleDelete(id)
    });
  };




  // Handle clear table filters and sorting only
  const handleClearTableFilters = () => {
    // Force table re-render to clear all filters and sorting state completely
    setTableKey(prev => prev + 1);

    message.success('Table filters and sorting cleared');
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = (columnKey: string, checked: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: checked
    }));
  };

  // Column configuration with display names
  const columnConfigMap = {
    id: 'Verification ID',
    doctorName: 'Doctor Name',
    patientName: 'Patient Name',
    surgeryDate: 'Surgery Date',
    onDemand: 'On Demand',
    status: 'Status',
    actions: 'Actions'
  };

  // Get ordered column config based on columnOrder
  const getOrderedColumnConfig = () => {
    return columnOrder.map(key => ({
      key,
      label: columnConfigMap[key as keyof typeof columnConfigMap]
    }));
  };

  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter and order columns based on visibility state and column order
  const getVisibleColumns = (originalColumns: ColumnsType<DemographicsData>) => {
    // First filter by visibility
    const visibleColumns = originalColumns.filter(column => {
      const columnKey = column.key as string;
      return columnVisibility[columnKey] !== false;
    });

    // Then sort by column order
    return visibleColumns.sort((a, b) => {
      const indexA = columnOrder.indexOf(a.key as string);
      const indexB = columnOrder.indexOf(b.key as string);
      return indexA - indexB;
    });
  };


  // Handle start verification
  const handleStartVerification = async (id: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/${id}/start-verification`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        message.success('Verification started successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
        fetchStatusStats(); // Refresh statistics
        fetchTotalCountStats(); // Refresh total count stats
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to start verification');
      }
    } catch (error) {
      message.error('Error starting verification');
    }
  };

  // Fetch insurance file info for a record
  const fetchInsuranceInfo = async (demographicsId: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${demographicsId}/info`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInsuranceFileInfo(prev => ({
          ...prev,
          [demographicsId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching insurance info:', error);
    }
  };

  // Handle insurance file upload
  const handleInsuranceFileUpload = async () => {
    if (!selectedRecordForInsurance || !insuranceFileState.selectedFile) {
      message.error('Please select a file to upload');
      return;
    }

    setInsuranceFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    const formData = new FormData();
    formData.append('insuranceFile', insuranceFileState.selectedFile);

    try {
      const response = await fetch(`/api/insurance/verifications/${selectedRecordForInsurance.id}/upload`, {
        method: 'POST',
        headers: getAuthHeadersForUpload(),
        body: formData,
      });

      if (response.ok) {
        message.success('Insurance file uploaded successfully');
        setIsInsuranceUploadModalVisible(false);
        setInsuranceFileState({
          selectedFile: null,
          isUploading: false,
          uploadError: null
        });
        setSelectedRecordForInsurance(null);
        // Refresh insurance info for this record
        await fetchInsuranceInfo(selectedRecordForInsurance.id);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to upload insurance file');
        setInsuranceFileState(prev => ({ ...prev, uploadError: errorData.message }));
      }
    } catch (error) {
      message.error('Error uploading insurance file');
      setInsuranceFileState(prev => ({ ...prev, uploadError: 'Network error' }));
    } finally {
      setInsuranceFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle accept insurance
  const handleAcceptInsurance = async (id: string) => {
    try {
      const response = await fetch(`/api/insurance/verifications/${id}/accept`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          acceptedBy: 'Insurance Verifier' // You can get this from user context
        })
      });

      if (response.ok) {
        message.success('Insurance verification accepted successfully');
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
        fetchStatusStats(); // Refresh statistics
        fetchTotalCountStats(); // Refresh total count stats
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to accept insurance verification');
      }
    } catch (error) {
      message.error('Error accepting insurance verification');
    }
  };

  // Handle reject insurance
  const handleRejectInsurance = async (id: string) => {
    Modal.confirm({
      title: 'Reject Insurance Verification',
      content: (
        <div>
          <p>Are you sure you want to reject this insurance verification?</p>
          <Input.TextArea
            placeholder="Please provide a reason for rejection..."
            id="rejection-reason"
            rows={3}
          />
        </div>
      ),
      okText: 'Reject',
      cancelText: 'Cancel',
      onOk: async () => {
        const reason = (document.getElementById('rejection-reason') as HTMLTextAreaElement)?.value || '';
        
        try {
          const response = await fetch(`/api/insurance/verifications/${id}/reject`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              rejectedBy: 'Insurance Verifier', // You can get this from user context
              rejectedReason: reason
            })
          });

          if (response.ok) {
            message.success('Insurance verification rejected successfully');
            fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
            fetchStatusStats(); // Refresh statistics
          } else {
            const errorData = await response.json();
            message.error(errorData.message || 'Failed to reject insurance verification');
          }
        } catch (error) {
          message.error('Error rejecting insurance verification');
        }
      }
    });
  };

  // Handle view insurance file
  const handleViewInsuranceFile = (id: string) => {
    const viewerUrl = `/view-pdf/insurance/${id}`;
    window.open(viewerUrl, '_blank');
  };


  // Table columns configuration for regular demographics
  const columns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Doctor Name',
      dataIndex: 'doctorName',
      key: 'doctorName',
      width: 200,
      sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
      filters: Array.from(new Set(demographicsData.map(item => item.doctorName))).map(name => ({
        text: name,
        value: name,
      })),
      onFilter: (value, record) => record.doctorName === value,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 200,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
     {
              title: 'Surgery Date',
        dataIndex: 'surgeryDate',
        key: 'surgeryDate',
        width: 130,
        render: (date, record) => {
          // Disable editing for Accepted, Rejected, and In Progress records
          const isEditable = record.status === 'Pending';

          if (isEditable) {
            return (
              <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(newDate: Dayjs | null) => handleSurgeryDateChange(record.id, newDate)}
                size="small"
                style={{ width: '100%' }}
              />
            );
          } else {
            // Display as read-only text for non-editable records
            return (
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                {date ? new Date(date).toLocaleDateString() : 'Not set'}
              </span>
            );
          }
        },
        sorter: (a, b) => new Date(a.surgeryDate).getTime() - new Date(b.surgeryDate).getTime(),
     },
    {
      title: 'On Demand',
      dataIndex: 'onDemand',
      key: 'onDemand',
      width: 120,
      render: (onDemand, record) => {
        const isUpdating = updatingOnDemand === record.id;
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Switch
              checked={onDemand}
              size="small"
              loading={isUpdating}
              onChange={(checked) => handleOnDemandChange(record.id, checked)}
              disabled={isUpdating}
            />
          </div>
        );
      },
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.onDemand === value,
      // Remove default sorter to maintain our custom sorting
      sorter: false,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => {
        const statusConfig = {
          'Pending': { color: '#faad14', icon: <ClockCircleOutlined />, text: 'Pending' },
          'In Progress': { color: '#1890ff', icon: <ExclamationCircleOutlined />, text: 'In Progress' },
          'Accepted': { color: '#52c41a', icon: <CheckCircleOutlined />, text: 'Accepted' },
          'Rejected': { color: '#ff4d4f', icon: <CloseCircleOutlined />, text: 'Rejected' }
        };

        const config = statusConfig[status as keyof typeof statusConfig];

        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '6px 10px',
            borderRadius: '8px',
            minHeight: '32px'
          }}>
            <Tag color={config.color} style={{ margin: 0 }}>
              {config.icon}
              <span style={{ marginLeft: '4px' }}>{config.text}</span>
            </Tag>
          </div>
        );
      },
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Accepted', value: 'Accepted' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
         {
       title: 'Actions',
       key: 'actions',
       width: 200,
       render: (_, record) => {
         // Disable editing and deletion for Accepted, Rejected, and In Progress records
         const isEditable = record.status === 'Pending';

         return (
           <div style={{ display: 'flex', gap: '4px' }}>

             {/* Insurance Verification Actions - Only for In Progress records */}
             {record.status === 'In Progress' && (
               <>
                 {/* Upload Insurance File Button */}
                 <Button
                   type="default"
                   size="small"
                   icon={<UploadOutlined />}
                   onClick={() => {
                     setSelectedRecordForInsurance(record);
                     setIsInsuranceUploadModalVisible(true);
                   }}
                   style={{ 
                     padding: '4px 8px',
                     minWidth: '32px',
                     height: '32px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}
                   title="Upload Insurance File"
                 />

                 {/* Show Accept/Reject buttons only if insurance file is uploaded */}
                 {insuranceFileInfo[record.id]?.hasInsuranceFile && (
                   <>
                     {/* Accept Button */}
                     <Button
                       type="primary"
                       size="small"
                       icon={<CheckCircleOutlined />}
                       onClick={() => {
                         Modal.confirm({
                           title: 'Accept Insurance Verification',
                           content: 'Are you sure you want to accept this insurance verification?',
                           okText: 'Yes, Accept',
                           cancelText: 'Cancel',
                           onOk: () => handleAcceptInsurance(record.id)
                         });
                       }}
                       style={{ 
                         padding: '4px 8px',
                         minWidth: '32px',
                         height: '32px',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         backgroundColor: '#52c41a',
                         borderColor: '#52c41a'
                       }}
                       title="Accept Insurance Verification"
                     />

                     {/* Reject Button */}
                     <Button
                       type="primary"
                       size="small"
                       icon={<CloseCircleOutlined />}
                       onClick={() => handleRejectInsurance(record.id)}
                       style={{ 
                         padding: '4px 8px',
                         minWidth: '32px',
                         height: '32px',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         backgroundColor: '#ff4d4f',
                         borderColor: '#ff4d4f'
                       }}
                       title="Reject Insurance Verification"
                     />

                     {/* View Insurance File Button */}
                     <Button
                       type="text"
                       size="small"
                       icon={<FileTextOutlined />}
                       onClick={() => handleViewInsuranceFile(record.id)}
                       style={{ 
                         padding: '4px 8px',
                         minWidth: '32px',
                         height: '32px',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         color: '#1890ff'
                       }}
                       title="View Insurance File"
                     />
                   </>
                 )}
               </>
             )}

             {/* View Insurance File Button - For Accepted and Rejected records */}
             {(record.status === 'Accepted' || record.status === 'Rejected') && (
               <Button
                 type="text"
                 size="small"
                 icon={<FileTextOutlined />}
                 onClick={() => handleViewInsuranceFile(record.id)}
                 style={{ 
                   padding: '4px 8px',
                   minWidth: '32px',
                   height: '32px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   color: '#1890ff'
                 }}
                 title="View Insurance File"
               />
             )}

             <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteWithConfirmation(record.id)}
                disabled={!isEditable || record.status === 'Accepted' || record.status === 'Rejected'}
                style={{ 
                  padding: '4px 8px',
                  minWidth: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (isEditable && record.status !== 'Accepted' && record.status !== 'Rejected') ? 1 : 0.5
                }}
                title={
                  record.status === 'Accepted' || record.status === 'Rejected' 
                    ? "Cannot delete - Record is finalized" 
                    : isEditable 
                      ? "Delete Record" 
                      : "Cannot delete - Record not in Pending status"
                }
              />
           </div>
         );
       },
     },
  ];

  // Columns for rejected records
  const rejectedColumns: ColumnsType<DemographicsData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => Number(a.id) - Number(b.id),
    },
    {
      title: 'Rejected Reason',
      dataIndex: 'rejectedReason',
      key: 'rejectedReason',
      width: 200,
      render: (text) => text || 'No reason provided',
      ellipsis: true,
    },
    {
      title: 'Patient Name',
      key: 'patientName',
      width: 80,
      render: (_, record) => {
        const fullName = [record.firstName, record.middleName, record.lastName]
          .filter(name => name && name.trim())
          .join(' ');
        return fullName || 'No name provided';
      },
      sorter: (a, b) => {
        const nameA = [a.firstName, a.middleName, a.lastName].filter(name => name && name.trim()).join(' ');
        const nameB = [b.firstName, b.middleName, b.lastName].filter(name => name && name.trim()).join(' ');
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Rejected By',
      dataIndex: 'rejectedBy',
      key: 'rejectedBy',
      width: 100,
      render: (text) => text || 'Unknown',
    },
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 100,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleViewPDF(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewInsuranceFile(record.id)}
            style={{ 
              padding: '4px 8px',
              minWidth: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1890ff'
            }}
            title="View Insurance File"
          />
        </div>
      ),
    },
  ];

  // Modal form handlers
  const handleFileChange = (info: any) => {
    const file = info.file;
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
      if (allowedTypes.includes(file.type)) {
        setFileState(prev => ({ ...prev, selectedFile: file, uploadStatus: '', uploadError: null }));
      } else {
        message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
        setFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
      }
    }
  };

  const handleDoctorChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedDoctor: value, selectedProcedure: '', uploadError: null }));
    // Fetch procedures for the selected doctor
    if (value) {
      fetchProceduresByDoctor(value);
    } else {
      setAvailableProcedures([]);
    }
  };

  const handleProcedureChange = (value: string) => {
    setFileState(prev => ({ ...prev, selectedProcedure: value, uploadError: null }));
  };

  const handleToggleChange = (checked: boolean) => {
    setFileState(prev => ({ ...prev, enableOnDemand: checked, uploadError: null }));
  };

  const handleModalSubmit = async () => {
    if (!fileState.selectedFile) {
      message.error('Please select a file first.');
      return;
    }

    if (!fileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!fileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    setFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === fileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : fileState.selectedDoctor;

    const formData = new FormData();
    formData.append('patientDetails', fileState.selectedFile);
    formData.append('doctor', doctorName); // Send doctor name for backward compatibility
    formData.append('doctorId', fileState.selectedDoctor); // Send doctor ID for future use
    formData.append('procedure', fileState.selectedProcedure);
    formData.append('enableOnDemand', fileState.enableOnDemand.toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

             if (response.ok) {
         let responseData;
         try {
           responseData = await response.json();
         } catch (parseError) {
           // If response is not JSON, treat it as plain text success
           responseData = { message: 'File uploaded successfully', isDuplicate: false };
         }

         if (responseData.isDuplicate || responseData.warning) {
           // Show warning for duplicates or surgery date issues
           message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
         } else {
           message.success(responseData.message || 'File uploaded successfully!');
         }

         setFileState({
           selectedFile: null,
           isUploading: false,
           uploadStatus: '',
           selectedDoctor: '',
           selectedProcedure: '',
           enableOnDemand: false,
           uploadError: null
         });
         setIsModalVisible(false);
         fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || ''); // Refresh data
         fetchStatusStats(); // Refresh statistics
               } else {
          // Handle backend errors properly
          let errorMessage = 'Upload failed. Please try again.';
          let validationErrors: string[] = [];

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;

            // Check if there are validation errors
            if (errorData.validationErrors && Array.isArray(errorData.validationErrors)) {
              validationErrors = errorData.validationErrors;
            }
          } catch (parseError) {
            // If error response is not JSON, try to get text
            try {
              const errorText = await response.text();
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              // If all else fails, use default message
              console.error('Could not parse error response:', textError);
            }
          }

          // Display validation errors if they exist
          if (validationErrors.length > 0) {
            const validationMessage = `File validation failed:\n${validationErrors.map(error => `• ${error}`).join('\n')}`;
            message.error({
              content: validationMessage,
              duration: 8, // Show longer for validation errors
              style: { whiteSpace: 'pre-line' }
            });
            errorMessage = validationMessage;
          } else {
            message.error(errorMessage);
          }

          // Set error state for display in modal
          setFileState(prev => ({ ...prev, uploadError: errorMessage }));
        }
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        message.error(`Upload failed: ${error.message}`);
      } else {
        message.error('Upload failed. Please check your connection.');
      }
    } finally {
      setFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    clearFileState();
  };

  const clearFileState = () => {
    setFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      enableOnDemand: false,
      uploadError: null
    });
  };

  const clearResubmitFileState = () => {
    setResubmitFileState({
      selectedFile: null,
      isUploading: false,
      uploadStatus: '',
      selectedDoctor: '',
      selectedProcedure: '',
      enableOnDemand: false,
      uploadError: null
    });
  };

  // Handle resubmit modal submit
  const handleResubmitSubmit = async () => {
    if (!resubmitRecord) return;

    if (!resubmitFileState.selectedDoctor) {
      message.error('Please select a doctor.');
      return;
    }

    if (!resubmitFileState.selectedProcedure) {
      message.error('Please select a procedure.');
      return;
    }

    setResubmitFileState(prev => ({ ...prev, isUploading: true, uploadError: null }));

    // Get the doctor name for display
    const selectedDoctor = doctors.find(d => d.employeeId === resubmitFileState.selectedDoctor);
    const doctorName = selectedDoctor ? selectedDoctor.name : resubmitFileState.selectedDoctor;

    const formData = new FormData();

    // Only append file if a new one is selected
    if (resubmitFileState.selectedFile) {
      formData.append('patientDetails', resubmitFileState.selectedFile);
    }

    formData.append('doctor', doctorName);
    formData.append('doctorId', resubmitFileState.selectedDoctor);
    formData.append('procedure', resubmitFileState.selectedProcedure);
    formData.append('enableOnDemand', resubmitFileState.enableOnDemand.toString());
    formData.append('keepExistingFile', (!resubmitFileState.selectedFile).toString());

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/resubmit/${resubmitRecord.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          responseData = { message: 'Record resubmitted successfully', isDuplicate: false };
        }

        if (responseData.isDuplicate || responseData.warning) {
          message.warning(responseData.warning || 'Potential Duplicate Found, Please check the duplicates tab.');
        } else {
          message.success('Record resubmitted successfully!');
        }

        clearResubmitFileState();
        setIsResubmitModalVisible(false);
        setResubmitRecord(null);
        fetchDemographicsData(currentPage, pageSize, smartSearchTerm, smartSearchField, selectedStatus || '');
        fetchStatusStats();
      } else {
        let errorMessage = 'Resubmit failed. Please try again.';
        let validationErrors: string[] = [];

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          validationErrors = errorData.errors || [];
        } catch (parseError) {
          // If response is not JSON, use the status text
          errorMessage = response.statusText || errorMessage;
        }

        if (validationErrors.length > 0) {
          message.error(
            <div>
              <div>{errorMessage}</div>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Resubmit error:', error);
      message.error('Network error. Please check your connection and try again.');
    } finally {
      setResubmitFileState(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Handle resubmit modal cancel
  const handleResubmitCancel = () => {
    setIsResubmitModalVisible(false);
    clearResubmitFileState();
    setResubmitRecord(null);
  };

  // Handle opening notes modal
  const handleOpenNotesModal = (record: DemographicsData) => {
    setSelectedRecord(record);
    setIsNotesModalVisible(true);
    setNewNoteText('');
    fetchNotes(record.id);
  };

  // Handle closing notes modal
  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
    setSelectedRecord(null);
    setNotes([]);
    setNewNoteText('');
  };

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !selectedRecord) return;
    await addNote(selectedRecord.id, newNoteText.trim());
  };

  // Handle note text area key press
  const handleNoteKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <div className="demographics-page">
             {/* First Container - Search and Add Button (15% height) */}
       <div className="search-container">
         <div className="search-content" style={{ position: 'relative', zIndex: 1 }}>
           <div className="search-inputs" style={{ pointerEvents: 'auto', zIndex: 5, flex: 1 }}>
             <SmartSearch
               onSearch={handleSmartSearch}
               style={{ marginBottom: 16, pointerEvents: 'auto' }}
             />

           </div>
         </div>
       </div>

      {/* Status Statistics Container */}
      <div className="status-stats-container" style={{ 
        padding: '16px', 
        backgroundColor: '#f5f5f5', 
        marginBottom: '16px',
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px' 
        }}>

          {/* Pending Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Pending')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Pending' ? '2px solid #faad14' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Pending' ? '#fffbe6' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileClock size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Pending</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Pending']} records</div>
              </div>
            </div>
          </Card>

          {/* In Progress Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('In Progress')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'In Progress' ? '2px solid #1890ff' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'In Progress' ? '#e6f7ff' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <Loader size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>In Progress</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['In Progress']} records</div>
              </div>
            </div>
          </Card>

          {/* Accepted Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Accepted')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Accepted' ? '2px solid #52c41a' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Accepted' ? '#f6ffed' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <FileCheck size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Accepted</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Accepted']} records</div>
              </div>
            </div>
          </Card>

          {/* Rejected Status Box */}
          <Card
            hoverable
            onClick={() => handleStatusFilterClick('Rejected')}
            style={{
              cursor: 'pointer',
              border: selectedStatus === 'Rejected' ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
              backgroundColor: selectedStatus === 'Rejected' ? '#fff2f0' : '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#ff4d4f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px'
              }}>
                <CircleX size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>Rejected</div>
                <div style={{ color: '#666', fontSize: '12px' }}>{statusStats['Rejected']} records</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

                           {/* Second Container - Table (remaining height) */}
        <div className="table-container">
          {/* Action Buttons Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
            </div>

            <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
              {/* Columns Button */}
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                size="middle"
                type="default"
                title="Column settings"
                data-columns-button
              >
                Columns
              </Button>

              {/* Columns Dropdown Menu */}
              {showColumnsDropdown && (
                <div
                  data-columns-dropdown
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px',
                    minWidth: '220px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    marginBottom: '12px',
                    color: '#262626',
                    borderBottom: '1px solid #f0f0f0',
                    paddingBottom: '8px'
                  }}>
                    Column Visibility
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {getOrderedColumnConfig().map((column) => (
                          <SortableItem
                            key={column.key}
                            id={column.key}
                            label={column.label}
                            checked={columnVisibility[column.key]}
                            onToggle={handleColumnVisibilityChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Clear Table Filters Button */}
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearTableFilters}
                size="middle"
                type="default"
                title="Clear table column filters and sorting"
              >
                Clear Table Filters
              </Button>
            </div>
          </div>

                     <Card title="Verifications Data" className="table-card">
                                                <Table
                key={tableKey}
                ref={tableRef}
                columns={selectedStatus === 'Rejected' ? (rejectedColumns as any) : (getVisibleColumns(columns) as any)}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  },
                }}
                scroll={{ x: 1400 }}
                size="middle"
              />
          </Card>
       </div>

      {/* Upload Modal */}
      <Modal
        title="Add New Demographics"
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        width={600}
        confirmLoading={fileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={fileState.selectedDoctor}
              onChange={handleDoctorChange}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={fileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={fileState.selectedProcedure}
              onChange={handleProcedureChange}
              style={{ width: '100%' }}
              disabled={!fileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Upload File:</label>
            <Upload
              key={modalKey}
              beforeUpload={() => false}
              onChange={handleFileChange}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
          </div>

                     <div className="form-item">
             <Switch
               checked={fileState.enableOnDemand}
               onChange={handleToggleChange}
             />
             <span style={{ marginLeft: '8px' }}>Enable on demand</span>
           </div>

                     {/* Error Display */}
           {fileState.uploadError && (
             <div className="form-item" style={{ marginTop: 16 }}>
               <div style={{ 
                 padding: '12px 16px', 
                 backgroundColor: '#fff2f0', 
                 border: '1px solid #ffccc7', 
                 borderRadius: '8px',
                 color: '#cf1322',
                 whiteSpace: 'pre-line'
               }}>
                 <strong>Upload Error:</strong><br />
                 {fileState.uploadError}
               </div>
             </div>
           )}
        </div>
      </Modal>

      {/* Resubmit Modal */}
      <Modal
        title="Resubmit Demographics Record"
        open={isResubmitModalVisible}
        onOk={handleResubmitSubmit}
        onCancel={handleResubmitCancel}
        width={600}
        confirmLoading={resubmitFileState.isUploading}
        okText="Resubmit"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: '8px',
            marginBottom: '16px',
            color: '#389e0d'
          }}>
            <strong>Resubmit Confirmation:</strong><br />
            You are about to resubmit record ID: {resubmitRecord?.id}. This will update the existing record with new data. You can optionally upload a new file or keep the current one. Please review and confirm your changes.
          </div>

          <div className="form-item">
            <label>Select Doctor:</label>
            <Select
              placeholder="Choose a doctor..."
              value={resubmitFileState.selectedDoctor}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedDoctor: value }))}
              style={{ width: '100%' }}
              loading={loadingDoctors}
            >
              {doctors.map((doctor, index) => (
                <Option key={index} value={doctor.employeeId}>{doctor.name}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Select Procedure:</label>
            <Select
              placeholder={resubmitFileState.selectedDoctor ? "Choose a procedure..." : "Please select a doctor first"}
              value={resubmitFileState.selectedProcedure}
              onChange={(value) => setResubmitFileState(prev => ({ ...prev, selectedProcedure: value }))}
              style={{ width: '100%' }}
              disabled={!resubmitFileState.selectedDoctor}
              loading={loadingProcedures}
            >
              {availableProcedures.map((procedure, index) => (
                <Option key={index} value={procedure}>{procedure}</Option>
              ))}
            </Select>
          </div>

          <div className="form-item">
            <label>Current File:</label>
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #d9d9d9', 
              borderRadius: '8px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <span>{resubmitRecord?.filename}</span>
              </div>
              <Button
                type="link"
                size="small"
                onClick={() => handleViewPDF(resubmitRecord?.id || '')}
                style={{ padding: '0 4px' }}
              >
                View
              </Button>
            </div>
          </div>

          <div className="form-item">
            <label>Upload New File (Optional):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
                  if (file.type && allowedTypes.includes(file.type)) {
                    setResubmitFileState(prev => ({ ...prev, selectedFile: file as File, uploadError: null }));
                  } else {
                    message.error('Invalid file type. Only PDF, Excel, and CSV files are allowed.');
                    setResubmitFileState(prev => ({ ...prev, selectedFile: null, uploadError: null }));
                  }
                }
              }}
              accept=".pdf,.xlsx,.csv,.txt"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose New File</Button>
            </Upload>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              Leave empty to keep the current file
            </div>
          </div>

          <div className="form-item">
            <Switch
              checked={resubmitFileState.enableOnDemand}
              onChange={(checked) => setResubmitFileState(prev => ({ ...prev, enableOnDemand: checked }))}
            />
            <span style={{ marginLeft: '8px' }}>Enable on demand</span>
          </div>

          {/* Error Display */}
          {resubmitFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322',
                whiteSpace: 'pre-line'
              }}>
                <strong>Upload Error:</strong><br />
                {resubmitFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        title={`Order Notes for #${selectedRecord?.id || ''}`}
        open={isNotesModalVisible}
        onCancel={handleCloseNotesModal}
        footer={null}
        width={600}
        centered
      >
        <div style={{ marginBottom: '16px' }}>
          <Input.TextArea
            placeholder="Add a new note and press Enter to save..."
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            onKeyPress={handleNoteKeyPress}
            rows={3}
            disabled={addingNote}
            style={{ marginBottom: '8px' }}
          />
          {addingNote && (
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <Spin size="small" /> Adding note...
            </div>
          )}
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notesLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
            </div>
          ) : notes.length === 0 ? (
            <Empty 
              description="No notes yet" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={notes}
              renderItem={(note) => (
                <List.Item
                  actions={[
                    <Button
                      key="delete"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Delete Note',
                          content: 'Are you sure you want to delete this note?',
                          okText: 'Yes',
                          cancelText: 'No',
                          onOk: () => deleteNote(note._id, note.orderId)
                        });
                      }}
                      size="small"
                    />
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <div style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        {note.text}
                      </div>
                    }
                    description={
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Added by {note.addedBy} • {new Date(note.createdAt).toLocaleString()}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Modal>

      {/* Insurance Upload Modal */}
      <Modal
        title={`Upload Insurance File - Record #${selectedRecordForInsurance?.id || ''}`}
        open={isInsuranceUploadModalVisible}
        onOk={handleInsuranceFileUpload}
        onCancel={() => {
          setIsInsuranceUploadModalVisible(false);
          setSelectedRecordForInsurance(null);
          setInsuranceFileState({
            selectedFile: null,
            isUploading: false,
            uploadError: null
          });
        }}
        width={500}
        confirmLoading={insuranceFileState.isUploading}
        okText="Upload"
        cancelText="Cancel"
      >
        <div className="modal-form">
          <div style={{ marginBottom: '16px' }}>
            <p><strong>Patient:</strong> {selectedRecordForInsurance ? 
              [selectedRecordForInsurance.firstName, selectedRecordForInsurance.middleName, selectedRecordForInsurance.lastName]
                .filter(name => name && name.trim())
                .join(' ') : ''
            }</p>
            <p><strong>Doctor:</strong> {selectedRecordForInsurance?.doctorName}</p>
            <p><strong>Procedure:</strong> {selectedRecordForInsurance?.procedureName}</p>
          </div>

          <div className="form-item">
            <label>Upload Insurance File (PDF only):</label>
            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file.originFileObj || info.file;
                if (file) {
                  if (file.type === 'application/pdf') {
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: file as File, 
                      uploadError: null 
                    }));
                  } else {
                    message.error('Only PDF files are allowed for insurance verification');
                    setInsuranceFileState(prev => ({ 
                      ...prev, 
                      selectedFile: null, 
                      uploadError: 'Only PDF files are allowed' 
                    }));
                  }
                }
              }}
              accept=".pdf"
              showUploadList={true}
            >
              <Button icon={<UploadOutlined />}>Choose PDF File</Button>
            </Upload>
          </div>

          {/* Error Display */}
          {insuranceFileState.uploadError && (
            <div className="form-item" style={{ marginTop: 16 }}>
              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#fff2f0', 
                border: '1px solid #ffccc7', 
                borderRadius: '8px',
                color: '#cf1322'
              }}>
                <strong>Upload Error:</strong><br />
                {insuranceFileState.uploadError}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VerificationsPage;
